/**
 * CR114: useAppAssets hook
 * 
 * CRUD operations for visual assets in app_assets table.
 */

import { useState, useCallback } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import type { 
  AppAsset, 
  CreateAssetInput, 
  UpdateAssetInput, 
  AssetType,
  AssetStatus,
} from "@/lib/asset-studio/types";
import { validateSvg, sanitizeSvg } from "@/lib/asset-studio/types";

const QUERY_KEY = "app-assets";

interface UseAppAssetsOptions {
  type?: AssetType;
  status?: AssetStatus;
}

export function useAppAssets(options: UseAppAssetsOptions = {}) {
  const queryClient = useQueryClient();
  const [saving, setSaving] = useState(false);

  // Fetch assets with optional filters
  const { data: assets = [], isLoading, error, refetch } = useQuery({
    queryKey: [QUERY_KEY, options.type, options.status],
    queryFn: async (): Promise<AppAsset[]> => {
      let query = supabase
        .from("app_assets")
        .select("*")
        .order("created_at", { ascending: false });

      if (options.type) {
        query = query.eq("type", options.type);
      }
      if (options.status) {
        query = query.eq("status", options.status);
      }

      const { data, error } = await query;
      
      if (error) {
        console.error("[useAppAssets] fetch error:", error);
        throw error;
      }
      
      return (data || []) as AppAsset[];
    },
  });

  // Create new asset
  const createAsset = useCallback(async (input: CreateAssetInput): Promise<AppAsset | null> => {
    // Validate SVG
    const validationError = validateSvg(input.svg);
    if (validationError) {
      toast.error(validationError);
      return null;
    }

    setSaving(true);
    try {
      const sanitizedSvg = sanitizeSvg(input.svg);
      
      const { data, error } = await supabase
        .from("app_assets")
        .insert({
          name: input.name,
          type: input.type,
          svg: sanitizedSvg,
          is_tileable: input.is_tileable ?? false,
          default_tint: input.default_tint ?? null,
          tint_mode: input.tint_mode ?? "none",
          tags: input.tags ?? [],
        })
        .select()
        .single();

      if (error) {
        console.error("[useAppAssets] create error:", error);
        toast.error("Failed to create asset");
        return null;
      }

      toast.success("Asset created");
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
      return data as AppAsset;
    } catch (err) {
      console.error("[useAppAssets] create exception:", err);
      toast.error("Failed to create asset");
      return null;
    } finally {
      setSaving(false);
    }
  }, [queryClient]);

  // Update existing asset
  const updateAsset = useCallback(async (id: string, input: UpdateAssetInput): Promise<boolean> => {
    // Validate SVG if being updated
    if (input.svg !== undefined) {
      const validationError = validateSvg(input.svg);
      if (validationError) {
        toast.error(validationError);
        return false;
      }
      input.svg = sanitizeSvg(input.svg);
    }

    setSaving(true);
    try {
      const { error } = await supabase
        .from("app_assets")
        .update({
          ...input,
          updated_at: new Date().toISOString(),
        })
        .eq("id", id);

      if (error) {
        console.error("[useAppAssets] update error:", error);
        toast.error("Failed to update asset");
        return false;
      }

      toast.success("Asset updated");
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
      return true;
    } catch (err) {
      console.error("[useAppAssets] update exception:", err);
      toast.error("Failed to update asset");
      return false;
    } finally {
      setSaving(false);
    }
  }, [queryClient]);

  // Archive asset (soft delete)
  const archiveAsset = useCallback(async (id: string): Promise<boolean> => {
    return updateAsset(id, { status: "archived" });
  }, [updateAsset]);

  // Restore archived asset
  const restoreAsset = useCallback(async (id: string): Promise<boolean> => {
    return updateAsset(id, { status: "active" });
  }, [updateAsset]);

  // Duplicate asset
  const duplicateAsset = useCallback(async (id: string): Promise<AppAsset | null> => {
    const source = assets.find(a => a.id === id);
    if (!source) {
      toast.error("Asset not found");
      return null;
    }

    return createAsset({
      name: `${source.name} (Copy)`,
      type: source.type,
      svg: source.svg,
      is_tileable: source.is_tileable,
      default_tint: source.default_tint,
      tint_mode: source.tint_mode,
      tags: source.tags,
    });
  }, [assets, createAsset]);

  // Hard delete (use sparingly)
  const deleteAsset = useCallback(async (id: string): Promise<boolean> => {
    setSaving(true);
    try {
      const { error } = await supabase
        .from("app_assets")
        .delete()
        .eq("id", id);

      if (error) {
        console.error("[useAppAssets] delete error:", error);
        toast.error("Failed to delete asset");
        return false;
      }

      toast.success("Asset deleted");
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
      return true;
    } catch (err) {
      console.error("[useAppAssets] delete exception:", err);
      toast.error("Failed to delete asset");
      return false;
    } finally {
      setSaving(false);
    }
  }, [queryClient]);

  // Get asset by ID (from cache)
  const getAssetById = useCallback((id: string): AppAsset | undefined => {
    return assets.find(a => a.id === id);
  }, [assets]);

  return {
    assets,
    loading: isLoading,
    saving,
    error,
    createAsset,
    updateAsset,
    archiveAsset,
    restoreAsset,
    duplicateAsset,
    deleteAsset,
    getAssetById,
    refetch,
  };
}
